/**
 * Persisted outbox for the consent-critical capture calls: the "someone
 * joined" consent addendum, `stopped`, withdraw and discard. Unlike the
 * best-effort event queue these survive an app kill or a long time offline
 * (home visits): saved per user in device storage, replayed in order when the
 * store binds, when the network returns and when the app comes back to the
 * foreground, with no attempt cap until the server gives a definitive answer.
 *
 * Entries hold ids, enums and (for an addendum) the joined person's role and
 * optional first name — the same data the server stores — and are only ever
 * sent with the signed-in token of the user they are keyed to.
 */

import type {
  SessionRecordingEventInput,
  SessionRecordingWithdrawnBy,
} from '@shared/sessions/session-recording'

export type OutboxCall =
  | { kind: 'event'; sessionUuid: string; recordingId: string; body: SessionRecordingEventInput }
  | { kind: 'withdraw'; sessionUuid: string; recordingId: string; by: SessionRecordingWithdrawnBy }
  | { kind: 'discard'; sessionUuid: string; recordingId: string }

interface OutboxEntry {
  id: string
  call: OutboxCall
}

interface PersistedOutbox {
  v: 1
  entries: OutboxEntry[]
}

export interface OutboxStorage {
  get(options: { key: string }): Promise<{ value: string | null }>
  set(options: { key: string; value: string }): Promise<void>
  remove(options: { key: string }): Promise<void>
}

export interface SessionRecordingOutbox {
  /** Load (and start replaying) the signed-in user's saved calls. */
  bind(userId: string): Promise<void>
  unbind(): void
  /** Save a call for the bound user and try to send it. */
  push(call: OutboxCall): Promise<void>
  /** Try now, skipping any backoff (online / foreground). */
  flush(): void
  size(): number
}

export const SESSION_RECORDING_OUTBOX_KEY_PREFIX = 'ascend-session-recording-outbox:'
const BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000]

function isOutboxCall(raw: unknown): raw is OutboxCall {
  const call = raw as Partial<OutboxCall> | null
  return (
    !!call &&
    typeof call.sessionUuid === 'string' &&
    typeof call.recordingId === 'string' &&
    (call.kind === 'discard' ||
      (call.kind === 'withdraw' && typeof (call as { by?: unknown }).by === 'string') ||
      (call.kind === 'event' && typeof (call as { body?: unknown }).body === 'object'))
  )
}

export function createSessionRecordingOutbox(deps: {
  storage: OutboxStorage
  send: (call: OutboxCall) => Promise<unknown>
  isRetryable: (err: unknown) => boolean
  schedule?: (fn: () => void, ms: number) => unknown
  cancel?: (handle: unknown) => void
}): SessionRecordingOutbox {
  const schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms))
  const cancel = deps.cancel ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>))
  let userId: string | null = null
  let entries: OutboxEntry[] = []
  let loading: Promise<void> = Promise.resolve()
  /** Bumped on bind/unbind: a pass for the previous user stops touching state. */
  let generation = 0
  let running = false
  let failures = 0
  let timer: unknown = null
  let seq = 0

  const keyFor = (uid: string) => `${SESSION_RECORDING_OUTBOX_KEY_PREFIX}${encodeURIComponent(uid)}`

  const clearTimer = () => {
    if (timer != null) cancel(timer)
    timer = null
  }

  async function persist(uid: string, snapshot: OutboxEntry[]): Promise<void> {
    try {
      if (snapshot.length === 0) await deps.storage.remove({ key: keyFor(uid) })
      else await deps.storage.set({ key: keyFor(uid), value: JSON.stringify({ v: 1, entries: snapshot }) })
    } catch {
      /* storage unavailable: the in-memory copy still sends this run */
    }
  }

  async function drain(): Promise<void> {
    if (running || !userId) return
    running = true
    clearTimer()
    const gen = generation
    const uid = userId
    try {
      await loading
      while (gen === generation && entries.length > 0) {
        const head = entries[0]
        try {
          await deps.send(head.call)
        } catch (err) {
          if (gen !== generation) return
          if (deps.isRetryable(err)) {
            const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)]
            failures += 1
            timer = schedule(() => {
              timer = null
              void drain()
            }, delay)
            return
          }
          // Definitive refusal (e.g. already approved): nothing more to send for it.
        }
        if (gen !== generation) return
        failures = 0
        entries = entries.filter((entry) => entry.id !== head.id)
        await persist(uid, entries)
      }
    } finally {
      running = false
    }
  }

  return {
    async bind(nextUserId) {
      generation += 1
      clearTimer()
      userId = nextUserId
      entries = []
      failures = 0
      const gen = generation
      loading = (async () => {
        try {
          const { value } = await deps.storage.get({ key: keyFor(nextUserId) })
          const parsed = value ? (JSON.parse(value) as Partial<PersistedOutbox>) : null
          if (gen !== generation || !parsed || parsed.v !== 1 || !Array.isArray(parsed.entries)) return
          const saved = parsed.entries.filter(
            (entry): entry is OutboxEntry => typeof entry?.id === 'string' && isOutboxCall(entry.call),
          )
          entries = [...saved, ...entries]
        } catch {
          /* unreadable → start empty */
        }
      })()
      await loading
      void drain()
    },

    unbind() {
      generation += 1
      clearTimer()
      userId = null
      entries = []
      failures = 0
    },

    async push(call) {
      if (!userId) return
      const uid = userId
      await loading
      if (uid !== userId) return
      seq += 1
      entries = [...entries, { id: `${Date.now().toString(36)}-${seq}`, call }]
      await persist(uid, entries)
      void drain()
    },

    flush() {
      if (entries.length > 0) void drain()
    },

    size: () => entries.length,
  }
}
